import { AppLocale, SupportedLanguage } from "@nativecall/shared";

type Copy = {
  appName: string;
  onboardingKicker: string;
  onboardingTitle: string;
  onboardingBody: string;
  howItWorksTitle: string;
  howItWorksStepOneTitle: string;
  howItWorksStepOneBody: string;
  howItWorksStepTwoTitle: string;
  howItWorksStepTwoBody: string;
  howItWorksStepThreeTitle: string;
  howItWorksStepThreeBody: string;
  appLanguageLabel: string;
  nativeLanguageSetupLabel: string;
  nativeLanguageSetupHint: string;
  nameLabel: string;
  continueCta: string;
  walletTitle: string;
  walletBody: string;
  topUpCta: string;
  readyToCall: string;
  creditLabel: string;
  reserveLabel: string;
  minutesUnit: string;
  minutesUsedLabel: string;
  dialerTitle: string;
  destinationLabel: string;
  callerLanguageLabel: string;
  calleeLanguageLabel: string;
  startCallCta: string;
  callHistory: string;
  noHistory: string;
  activeCall: string;
  endCall: string;
  mute: string;
  speaker: string;
  lowConfidence: string;
  transcript: string;
  transcriptYouLabel: string;
  transcriptThemLabel: string;
  statusLabel: string;
  callingState: string;
  ringingState: string;
  connectingState: string;
  preparingAudioState: string;
  liveTranslationState: string;
  failedState: string;
  endingState: string;
  callingHint: string;
  liveCallHint: string;
  transcriptWaiting: string;
  signUpError: string;
  genericError: string;
  billingModeLabel: string;
  disclosureLabel: string;
  callSummary: string;
  balanceTooLow: string;
  providerStatusLabel: string;
  legalConsentLabel: string;
  callConsentLabel: string;
  acceptableUseLabel: string;
  emergencyNotice: string;
  shortCodeBlockedNotice: string;
  legalLinksTitle: string;
  privacyPolicyLabel: string;
  termsLabel: string;
  supportLabel: string;
  deleteAccountLabel: string;
  deleteAccountBody: string;
  deleteAccountConfirmLabel: string;
  closeLabel: string;
  cancelLabel: string;
  supportContactLabel: string;
  openWebPageLabel: string;
  openLabel: string;
  restorePurchasesCta: string;
  demoHeaderLabel: string;
  demoTapForSound: string;
  demoPlaying: string;
  demoYouLabel: string;
  demoRestaurantLabel: string;
  demoSaidTag: string;
  demoHeardTag: string;
  demoFlashCaption: string;
  demoDismissA11y: string;
};

const copyByLocale: Record<AppLocale, Copy> = {
  en: {
    appName: "NativeCall",
    onboardingKicker: "Traveler-first translated calls",
    onboardingTitle: "Call Japan in your language.",
    onboardingBody:
      "Learn the flow, choose your language and app language, then place an in-app translated call to a Japanese number.",
    howItWorksTitle: "How it works",
    howItWorksStepOneTitle: "1. You speak naturally",
    howItWorksStepOneBody:
      "Pick your language first. NativeCall uses it as your default speaking language when a call starts.",
    howItWorksStepTwoTitle: "2. They hear their language",
    howItWorksStepTwoBody:
      "After a short Japanese AI disclosure, the callee hears translated audio on a normal phone call. They do not need the app.",
    howItWorksStepThreeTitle: "3. You hear them back",
    howItWorksStepThreeBody:
      "When they reply, NativeCall translates their speech back into your language and shows the transcript in the call screen.",
    appLanguageLabel: "App language",
    nativeLanguageSetupLabel: "Your language",
    nativeLanguageSetupHint:
      "NativeCall translates from your language into Japanese and back. You can still change it before each call.",
    nameLabel: "Your name",
    continueCta: "Create account",
    walletTitle: "Wallet and minutes",
    walletBody:
      "Prepaid minutes fund each translated call. Pick a pack when you are ready to top up.",
    topUpCta: "Top up minutes",
    readyToCall: "Ready to call",
    creditLabel: "Balance",
    reserveLabel: "Minimum reserve",
    minutesUnit: "min",
    minutesUsedLabel: "min used",
    dialerTitle: "Start a translated call",
    destinationLabel: "Japanese number",
    callerLanguageLabel: "Your language",
    calleeLanguageLabel: "Japanese",
    startCallCta: "Place call",
    callHistory: "Recent calls",
    noHistory: "No calls yet. Your transcript history will appear here.",
    activeCall: "Active call",
    endCall: "End call",
    mute: "Mute",
    speaker: "Speaker",
    lowConfidence: "Low-confidence translation",
    transcript: "Transcript",
    transcriptYouLabel: "You",
    transcriptThemLabel: "Them",
    statusLabel: "Status",
    callingState: "Calling",
    ringingState: "Ringing",
    connectingState: "Connecting audio",
    preparingAudioState: "Preparing microphone",
    liveTranslationState: "Live translation",
    failedState: "Call failed",
    endingState: "Ending call",
    callingHint: "Keep the line open while we connect both sides.",
    liveCallHint: "Speak naturally. Translation audio plays in both directions.",
    transcriptWaiting: "Live translated transcript will appear here once speech is detected.",
    signUpError: "We could not create your account.",
    genericError: "Something failed. Check the API server and try again.",
    billingModeLabel: "Billing",
    disclosureLabel: "Automated Japanese disclosure will play first.",
    callSummary: "Summary",
    balanceTooLow: "Top up before starting a translated call.",
    providerStatusLabel: "Provider",
    legalConsentLabel: "I agree to the Terms of Use and Privacy Policy.",
    callConsentLabel:
      "I agree that my microphone audio and transcripts will be sent to OpenAI (third-party AI) for real-time translation and to Twilio for telephony. Without this, the call cannot run.",
    acceptableUseLabel:
      "I will not use NativeCall for prank, deceptive, anonymous, abusive, or harassing calls.",
    emergencyNotice:
      "Not for emergency, medical, legal, or safety-critical use.",
    shortCodeBlockedNotice:
      "Japanese emergency and short service numbers are blocked.",
    legalLinksTitle: "Privacy, support, and account controls",
    privacyPolicyLabel: "Privacy Policy",
    termsLabel: "Terms of Use",
    supportLabel: "Support",
    deleteAccountLabel: "Delete account",
    deleteAccountBody:
      "This removes your account, wallet state, and stored transcript history. Active calls must be ended first.",
    deleteAccountConfirmLabel: "Delete account permanently",
    closeLabel: "Close",
    cancelLabel: "Cancel",
    supportContactLabel: "Support contact",
    openWebPageLabel: "Open web page",
    openLabel: "Open",
    restorePurchasesCta: "Restore purchases",
    demoHeaderLabel: "Live translation demo",
    demoTapForSound: "🔇 Tap for sound",
    demoPlaying: "🔊 Playing",
    demoYouLabel: "You",
    demoRestaurantLabel: "Restaurant",
    demoSaidTag: "said",
    demoHeardTag: "heard",
    demoFlashCaption: "NATIVECALL translates live — both sides",
    demoDismissA11y: "Dismiss translation demo",
  },
  zh: {
    appName: "NativeCall",
    onboardingKicker: "為旅日使用者打造的即時通話翻譯",
    onboardingTitle: "用你的語言撥打日本電話。",
    onboardingBody:
      "先了解通話流程，再選擇你的語言與 App 語言，之後就能直接在 App 內撥打可即時翻譯的日本電話。",
    howItWorksTitle: "運作方式",
    howItWorksStepOneTitle: "1. 你自然說話",
    howItWorksStepOneBody:
      "先選擇你的語言。NativeCall 會把它當作新通話的預設說話語言。",
    howItWorksStepTwoTitle: "2. 對方會聽到自己的語言",
    howItWorksStepTwoBody:
      "在一段簡短的日語 AI 提示之後，對方會在一般電話通話中聽到翻譯語音，不需要安裝 App。",
    howItWorksStepThreeTitle: "3. 你會聽到翻回來的內容",
    howItWorksStepThreeBody:
      "當對方回話時，NativeCall 會把語音翻回你的語言，並在通話畫面中同步顯示逐字稿。",
    appLanguageLabel: "App 語言",
    nativeLanguageSetupLabel: "你的語言",
    nativeLanguageSetupHint:
      "NativeCall 會把你的語言翻成日文，再把日文回譯給你。你之後也可以在每通電話前再修改。",
    nameLabel: "你的姓名",
    continueCta: "建立帳戶",
    walletTitle: "錢包與分鐘",
    walletBody:
      "每通翻譯電話會扣抵預付分鐘。準備好時再選擇一個分鐘包進行儲值。",
    topUpCta: "儲值分鐘",
    readyToCall: "可以撥打",
    creditLabel: "餘額",
    reserveLabel: "最低保留",
    minutesUnit: "分鐘",
    minutesUsedLabel: "分鐘已使用",
    dialerTitle: "開始翻譯通話",
    destinationLabel: "日本號碼",
    callerLanguageLabel: "你的語言",
    calleeLanguageLabel: "日文",
    startCallCta: "開始通話",
    callHistory: "最近通話",
    noHistory: "目前還沒有通話記錄。通話後的雙語逐字稿會顯示在這裡。",
    activeCall: "目前通話",
    endCall: "結束通話",
    mute: "靜音",
    speaker: "擴音",
    lowConfidence: "本輪翻譯信心偏低",
    transcript: "逐字稿",
    transcriptYouLabel: "你",
    transcriptThemLabel: "對方",
    statusLabel: "狀態",
    callingState: "撥號中",
    ringingState: "響鈴中",
    connectingState: "正在連接音訊",
    preparingAudioState: "正在準備麥克風",
    liveTranslationState: "即時翻譯中",
    failedState: "通話失敗",
    endingState: "正在結束通話",
    callingHint: "請先保持在線，系統正在連接雙方通話。",
    liveCallHint: "自然說話即可，系統會雙向播放翻譯語音。",
    transcriptWaiting: "偵測到語音後，這裡會顯示即時雙語逐字稿。",
    signUpError: "無法建立帳戶。",
    genericError: "操作失敗。請檢查 API 服務後再試一次。",
    billingModeLabel: "收費模式",
    disclosureLabel: "系統會先播放一段日語自動提示。",
    callSummary: "摘要",
    balanceTooLow: "請先儲值，再發起翻譯通話。",
    providerStatusLabel: "語音服務",
    legalConsentLabel: "我同意使用條款與隱私權政策。",
    callConsentLabel:
      "我同意將本次通話的麥克風語音與逐字稿傳送至 OpenAI（第三方 AI）進行即時翻譯，並透過 Twilio 處理通話。若不同意，無法進行通話。",
    acceptableUseLabel:
      "我不會把 NativeCall 用於惡作劇、欺騙、匿名、辱罵或騷擾通話。",
    emergencyNotice: "請勿用於緊急、醫療、法律或任何安全關鍵用途。",
    shortCodeBlockedNotice: "日本的緊急號碼與短碼服務號碼會被阻擋。",
    legalLinksTitle: "隱私、支援與帳戶控制",
    privacyPolicyLabel: "隱私權政策",
    termsLabel: "使用條款",
    supportLabel: "客服支援",
    deleteAccountLabel: "刪除帳戶",
    deleteAccountBody:
      "此操作會刪除帳戶、錢包狀態與已保存的逐字稿歷史。若仍有進行中的通話，需先結束通話。",
    deleteAccountConfirmLabel: "永久刪除帳戶",
    closeLabel: "關閉",
    cancelLabel: "取消",
    supportContactLabel: "客服聯絡方式",
    openWebPageLabel: "開啟網頁",
    openLabel: "開啟",
    restorePurchasesCta: "還原購買",
    demoHeaderLabel: "即時翻譯示範",
    demoTapForSound: "🔇 點一下聽聲音",
    demoPlaying: "🔊 播放中",
    demoYouLabel: "你",
    demoRestaurantLabel: "餐廳",
    demoSaidTag: "說",
    demoHeardTag: "聽見",
    demoFlashCaption: "NATIVECALL 雙向即時翻譯",
    demoDismissA11y: "關閉翻譯示範",
  },
};

export const languageLabels: Record<
  AppLocale,
  Record<SupportedLanguage, string>
> = {
  en: {
    en: "English",
    ja: "Japanese",
    zh: "Chinese",
  },
  zh: {
    en: "英文",
    ja: "日文",
    zh: "中文",
  },
};

export function getCopy(locale: AppLocale) {
  return copyByLocale[locale] ?? copyByLocale.en;
}
